import { createConsumer } from '@rails/actioncable';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

// Keep this safely below OnlineStatusTracker::PRESENCE_DURATION (default 20s)
// to avoid edge-case expirations due to timer drift.
const PRESENCE_INTERVAL = 15000;
const RECONNECT_INTERVAL = 1000;

class BaseActionCableConnector {
  static isDisconnected = false;

  constructor(app, pubsubToken, websocketHost = '') {
    const normalizeWebsocketURL = host => {
      if (!host) return undefined;
      const h = String(host).trim().replace(/\/+$/, '');
      // Accept full urls like wss://x/ws/cable or relative paths like /ws
      if (h.endsWith('/cable')) return h;
      return `${h}/cable`;
    };

    const websocketURL = normalizeWebsocketURL(websocketHost);
    console.log('[ActionCable] Connecting to:', websocketURL || 'default URL');

    this.consumer = createConsumer(websocketURL);
    this.subscription = this.consumer.subscriptions.create(
      {
        channel: 'RoomChannel',
        pubsub_token: pubsubToken,
        account_id: app.$store.getters.getCurrentAccountId,
        user_id: app.$store.getters.getCurrentUserID,
      },
      {
        connected: () => {
          console.log('[ActionCable] Connected successfully');
          BaseActionCableConnector.isDisconnected = false;
        },
        updatePresence() {
          this.perform('update_presence');
        },
        received: this.onReceived,
        disconnected: () => {
          console.log('[ActionCable] Disconnected');
          BaseActionCableConnector.isDisconnected = true;
          this.onDisconnected();
          this.initReconnectTimer();
        },
        rejected: () => {
          console.error('[ActionCable] Connection rejected');
          BaseActionCableConnector.isDisconnected = true;
          this.onDisconnected();
          this.initReconnectTimer();
        },
      }
    );
    this.app = app;
    this.events = {};
    this.reconnectTimer = null;
    this.presenceInterval = null;
    this.isAValidEvent = () => true;
    this.startPresenceInterval();
  }

  checkConnection() {
    const isConnectionActive = this.consumer.connection.isOpen();
    const isReconnected =
      BaseActionCableConnector.isDisconnected && isConnectionActive;
    if (isReconnected) {
      this.clearReconnectTimer();
      this.onReconnect();
      BaseActionCableConnector.isDisconnected = false;
    } else {
      this.initReconnectTimer();
    }
  }

  clearReconnectTimer = () => {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  };

  clearPresenceInterval = () => {
    if (this.presenceInterval) {
      clearInterval(this.presenceInterval);
      this.presenceInterval = null;
    }
  };

  startPresenceInterval = () => {
    this.clearPresenceInterval();
    this.presenceInterval = setInterval(() => {
      const isConnectionActive = this.consumer?.connection?.isOpen?.();
      if (!isConnectionActive || !this.subscription) return;
      this.subscription.updatePresence();
    }, PRESENCE_INTERVAL);
  };

  initReconnectTimer = () => {
    this.clearReconnectTimer();
    this.reconnectTimer = setTimeout(() => {
      this.checkConnection();
    }, RECONNECT_INTERVAL);
  };

  // eslint-disable-next-line class-methods-use-this
  onReconnect = () => {};

  // eslint-disable-next-line class-methods-use-this
  onDisconnected = () => {};

  disconnect() {
    this.clearReconnectTimer();
    this.clearPresenceInterval();
    this.consumer.disconnect();
  }

  onReceived = ({ event, data } = {}) => {
    try {
      emitter.emit(BUS_EVENTS.WEBSOCKET_EVENT_RECEIVED, { event, data });
    } catch (e) {
      // noop
    }
    if (this.isAValidEvent(data)) {
      if (this.events[event] && typeof this.events[event] === 'function') {
        this.events[event](data);
      }
    }
  };
}

export default BaseActionCableConnector;
